import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { IS_DESKTOP_SCALE } from "../../../AtomStates";

// Assets
import backgroundIdiom from "../../../../assets/images/Background/background_idiom.png";
import idiomCheckIcon from "../../../../assets/images/idiom_check.svg";
import idiomTitleIcon from "../../../../assets/images/idiom_title.svg";
import gyeongwonLogo from "../../../../assets/assocation_images/gyeongwon_middle_logo.png";

const DUMMY_IDIOM = {
  title: ["사자성어", "일석이조"],
  idiom: {
    hanja: "一石二鳥",
    hangul: "일석이조",
    characters: [
      { hanja: "一", eum: "일", hun: "한" },
      { hanja: "石", eum: "석", hun: "돌" },
      { hanja: "二", eum: "이", hun: "두" },
      { hanja: "鳥", eum: "조", hun: "새" }
    ],
    meaning: "한 가지 일로 두 가지 이익을 얻음을 의미합니다.",
    explanation: "돌 한 개를 던져 새 두 마리를 잡는다는 뜻으로, 한 번의 노력으로 두 가지 성과를 거둘 때 사용하는 말이에요.",
    quiz: {
      question: "돌 하나로 새 두 마리를 잡는다는 뜻의 사자성어는 무엇일까요?",
      initial: "ㅇㅅㅇㅈ"
    }
  }
};

const MoleculeLearningIdiom = ({
  idiom = DUMMY_IDIOM
}) => {
  const [isDesktopScale] = useAtom(IS_DESKTOP_SCALE);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Clock Update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return { time: `${hours}:${minutes}`, ampm };
  };

  const { time, ampm } = formatTime(currentTime);

  // Data Parsing
  let hanjas = [];
  let sounds = [];
  let meanings = [];
  let explanation = "";
  let interpretation = "";

  // Exact structure check: idiom.idiom.characters
  const currentData = idiom || DUMMY_IDIOM;
  const nestedIdiom = currentData.idiom;

  if (nestedIdiom && nestedIdiom.characters) {
    hanjas = nestedIdiom.characters.map(c => c.hanja);
    sounds = nestedIdiom.characters.map(c => c.eum);
    meanings = nestedIdiom.characters.map(c => c.hun);
    explanation = nestedIdiom.meaning;
    interpretation = nestedIdiom.explanation;
  }

  return (
    <StyledMainContainer isDesktopScale={isDesktopScale}>
      {/* Header */}
      <StyledHeader>
        <StyledSchoolLogo>
          <img src={gyeongwonLogo} alt="School Logo" />
        </StyledSchoolLogo>
        
        <StyledTitleWrapper>
          <StyledBrushStroke src={idiomTitleIcon} alt="Brush Stroke" />
          <StyledHeaderText>배우기 좋은 사자성어 고사성어</StyledHeaderText>
        </StyledTitleWrapper>

        <StyledClockWrapper>
          <StyledTime>{time}</StyledTime>
          <StyledAmPm>{ampm}</StyledAmPm>
        </StyledClockWrapper>
      </StyledHeader>

      {/* Main Content: Hanja Characters */}
      <StyledHanjaContainer>
        {hanjas.map((hanja, index) => (
          <StyledHanjaItem key={index}>
            <StyledHanjaChar>{hanja}</StyledHanjaChar>
            <StyledHanjaMeaning>{meanings[index] || ""}</StyledHanjaMeaning>
            <StyledHanjaSound>{sounds[index] || ""}</StyledHanjaSound>
          </StyledHanjaItem>
        ))}
      </StyledHanjaContainer>

      {/* Footer Content: Explanations */}
      <StyledExplanations>
        <StyledExplanationSection>
          <StyledSectionHeader>
            <img src={idiomCheckIcon} alt="Check" />
            <StyledSectionTitle>한자 풀이</StyledSectionTitle>
          </StyledSectionHeader>
          <StyledSectionBody>
            {explanation || "한자 풀이 내용이 없습니다."}
          </StyledSectionBody>
        </StyledExplanationSection>

        <StyledExplanationSection>
          <StyledSectionHeader>
            <img src={idiomCheckIcon} alt="Check" />
            <StyledSectionTitle>해석</StyledSectionTitle>
          </StyledSectionHeader>
          <StyledSectionBody>
            {interpretation || "해석 내용이 없습니다."}
          </StyledSectionBody>
        </StyledExplanationSection>
      </StyledExplanations>
    </StyledMainContainer>
  );
};

export default MoleculeLearningIdiom;

// --- Styled Components ---

const StyledMainContainer = styled.div`
  width: 3840px;
  height: 2160px;
  background: url(${backgroundIdiom}) no-repeat center center;
  background-size: cover;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
`;

const StyledHeader = styled.div`
  position: absolute;
  top: 100px;
  left: 0;
  width: 100%;
  padding: 70px 240px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
`;

const StyledSchoolLogo = styled.div`
  img {
    height: 120px;
    object-fit: contain;
  }
`;

const StyledTitleWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledBrushStroke = styled.img`
  width: 1200px;
  height: 180px;
  object-fit: contain;
`;

const StyledHeaderText = styled.div`
  position: absolute;
  font-family: "Pretendard";
  font-size: 72px;
  font-weight: 600;
  color: white;
  text-align: center;
  transform: translateY(-12px);
`;

const StyledClockWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  color: #323232;
`;

const StyledTime = styled.div`
  font-family: "Pretendard";
  font-size: 96px;
  font-weight: 600;
  line-height: 1;
`;

const StyledAmPm = styled.div`
  font-family: "Pretendard";
  font-size: 56px;
  font-weight: 500;
  line-height: 1;
  padding-bottom: 8px;
`;

const StyledHanjaContainer = styled.div`
  position: absolute;
  top: 400px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 500px;
  justify-content: center;
  align-items: center;
`;

const StyledHanjaItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #472642;
`;

const StyledHanjaChar = styled.div`
  font-family: "Hangeuljaemin4.0", serif;
  font-size: 250px;
  line-height: 1.2;
`;

const StyledHanjaMeaning = styled.div`
  font-family: "Pretendard";
  font-size: 80px;
  font-weight: 500;
  margin-top: 30px;
`;

const StyledHanjaSound = styled.div`
  font-family: "Pretendard";
  font-size: 160px;
  font-weight: 600;
  margin-top: 10px;
`;

const StyledExplanations = styled.div`
  position: absolute;
  top: 1100px;
  left: 50%;
  transform: translateX(-50%);
  width: 3370px;
  display: flex;
  flex-direction: column;
  gap: 80px;
`;

const StyledExplanationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  img {
    width: 130px;
    height: 130px;
  }
`;

const StyledSectionTitle = styled.div`
  font-family: "Pretendard";
  font-size: 84px;
  font-weight: 700;
  color: #323232;
`;

const StyledSectionBody = styled.div`
  font-family: "Pretendard";
  font-size: 72px;
  font-weight: 500;
  color: #323232;
  line-height: 1.6;
  padding-left: 160px;
  white-space: pre-wrap;
  text-align: left;
`;


